import { AnimData } from "../core/LottieAnimation";
import ImageOverride from "../core/overrides/ImageOverride";
import ResizableScene from "../core/ResizableScene";
import F84Game from "../Game";
import BaseURL from "../utils/BaseURL";
import CacheBuster from "../utils/CacheBuster";

export default class LoaderScene extends ResizableScene {
	private _audioKeys: string[];

	preload(): void {
		this.load.on(Phaser.Loader.Events.FILE_COMPLETE, this.loadUpdate, this);
		this.load.setBaseURL(BaseURL.getBaseURL());
		this.events.on(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
	}

	create(): void {
		this.load.off(Phaser.Loader.Events.FILE_COMPLETE, this.loadUpdate, this);
	}

	protected loadUpdate(): void {
		console.log("override me");
	}

	loadImages(prefix: string, map: { [k: string]: string }, suffix = ".png"): void {
		for (const name in map) {
			this.loadImage(name, prefix + map[name] + suffix);
		}
	}

	loadImage(name: string, path: string, hasLowQuality = true): void {
		if (hasLowQuality) {
			path = path.replace(".png", F84Game.Instance.quality.suffix + ".png");
		}
		this.load.image(name, path);

		if (hasLowQuality) {
			this.load.once(Phaser.Loader.Events.COMPLETE, (): void => {
				const texture = this.textures.get(name);
				ImageOverride.overrideTexture(texture);
			});
		}
	}

	loadVideo(name: string, path: string): void {
		this.load.video(name, path);
	}

	loadJson(name: string, path: string, hasLowQuality = true): void {
		if (hasLowQuality) {
			path = path.replace(".json", F84Game.Instance.quality.suffix + ".json");
		}
		const bustedPath = CacheBuster.Bust(path);
		this.load.json(name, bustedPath);
	}

	loadSpritesheet(
		key: string | Phaser.Types.Loader.FileTypes.SpriteSheetFileConfig | Phaser.Types.Loader.FileTypes.SpriteSheetFileConfig[],
		url?: string,
		frameConfig?: Phaser.Types.Loader.FileTypes.ImageFrameConfig
	): void {
		// if (true) {
		url = url.replace(".png", F84Game.Instance.quality.suffix + ".png");
		// }
		this.load.spritesheet(key, url, frameConfig);
	}

	loadAtlas(key: string, textureURL: string, atlasURL: string, hasLowQuality = true): void {
		if (hasLowQuality) {
			textureURL = textureURL.replace(".png", F84Game.Instance.quality.suffix + ".png");
			atlasURL = atlasURL.replace(".json", F84Game.Instance.quality.suffix + ".json");
		}
		this.load.atlas(key, textureURL, atlasURL);
		if (hasLowQuality) {
			this.load.once(Phaser.Loader.Events.COMPLETE, (): void => {
				const texture = this.textures.get(key);
				ImageOverride.overrideTexture(texture);
			});
		}
	}

	loadMultiAtlas(key: string, atlasURL: string, baseURL: string, hasLowQuality = false): void {
		if (hasLowQuality) {
			atlasURL = atlasURL.replace(".json", F84Game.Instance.quality.suffix + ".json");
		}
		this.load.multiatlas(key, atlasURL, baseURL);
		if (hasLowQuality) {
			this.load.once(Phaser.Loader.Events.COMPLETE, (): void => {
				const texture = this.textures.get(key);
				ImageOverride.overrideTexture(texture);
			});
		}
	}

	loadAnimation(name: string, path: string, img: string, json: string): void {
		this.loadAtlas(name, path + img + ".png", path + json + ".json");
	}

	loadAudio(prefix: string, map: { [k: string]: string }, ext = ".mp3"): void {
		if (this._audioKeys == null) this._audioKeys = [];
		for (const name in map) {
			this.load.audio(name, [prefix + map[name] + ext]);
			this._audioKeys.push(name);
		}
	}

	loadLottie(path: string, name: string, width: number, height: number, spritesheet = false, backgroundOnly = false): void {
		const animDataPath = path + name + "/data.json";

		if (spritesheet) {
			const spritesheetPath = path + name + "/spritesheet.png";
			const spritesheetDataPath = path + name + "/images/spritesheet.json";

			const spritesheetDataLoaded = (key: string, type: string, data: SpriteAtlas): void => {
				if (key == name && type == "json") {
					this.load.off("filecomplete", spritesheetDataLoaded);
					this.load.lottie(name, animDataPath, width, height, spritesheetPath, data, backgroundOnly);
				}
			};
			this.load.on("filecomplete", spritesheetDataLoaded);
			this.loadJson(name, spritesheetDataPath, false);
		} else {
			this.load.lottie(name, animDataPath, width, height, null, null, backgroundOnly);
		}
	}

	loadLottieAnimation(path: string, name: string, spritesheet = true): void {
		if (spritesheet) {
			const spritesheetPath = path + name + "/images/spritesheet.png";
			const spritesheetDataPath = path + name + "/images/spritesheet.json";
			this.loadAtlas(name + "-atlas", spritesheetPath, spritesheetDataPath);
		} else {
			const animDataLoaded = (key: string, type: string, data: AnimData): void => {
				if (key == name + "-animData" && type == "json") {
					this.load.off("filecomplete", animDataLoaded);
					for (const asset of data.assets) {
						if (asset.p == null) continue;
						const imagePath = path + name + "/images/" + asset.p;
						this.loadImage(name + "-" + asset.p, imagePath);
					}
				}
			};
			this.load.on("filecomplete", animDataLoaded);
		}

		const animDataPath = path + name + "/data.json";
		this.loadJson(name + "-animData", animDataPath);
	}

	loadSpine(name: string, path: string, filename: string, preMultipliedAlpha?: boolean): void {
		this.load.spine(name, path + filename + ".json", path + filename + ".atlas.txt", preMultipliedAlpha);
	}
}
